import { useState } from 'react'
import { supabase } from './supabaseClient'
import { useNavigate } from 'react-router-dom'
import { trackLead, trackCompleteRegistration, trackStartTrial } from './utils/metaPixel'
import { Icon } from '@iconify/react'

export default function Signup() {
  const navigate = useNavigate()

  // Estados do formulário
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [cpfCnpj, setCpfCnpj] = useState('')

  // Estados de controle
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [emailEnviado, setEmailEnviado] = useState(false)

  // Função para aplicar máscara de CPF ou CNPJ
  const formatarCpfCnpj = (value) => {
    const numeros = value.replace(/\D/g, '')
    if (numeros.length <= 11) {
      // CPF: 000.000.000-00
      if (numeros.length <= 3) {
        return numeros
      } else if (numeros.length <= 6) {
        return `${numeros.slice(0, 3)}.${numeros.slice(3)}`
      } else if (numeros.length <= 9) {
        return `${numeros.slice(0, 3)}.${numeros.slice(3, 6)}.${numeros.slice(6)}`
      } else {
        return `${numeros.slice(0, 3)}.${numeros.slice(3, 6)}.${numeros.slice(6, 9)}-${numeros.slice(9)}`
      }
    } else {
      // CNPJ: 00.000.000/0000-00
      if (numeros.length <= 2) {
        return numeros
      } else if (numeros.length <= 5) {
        return `${numeros.slice(0, 2)}.${numeros.slice(2)}`
      } else if (numeros.length <= 8) {
        return `${numeros.slice(0, 2)}.${numeros.slice(2, 5)}.${numeros.slice(5)}`
      } else if (numeros.length <= 12) {
        return `${numeros.slice(0, 2)}.${numeros.slice(2, 5)}.${numeros.slice(5, 8)}/${numeros.slice(8)}`
      } else {
        return `${numeros.slice(0, 2)}.${numeros.slice(2, 5)}.${numeros.slice(5, 8)}/${numeros.slice(8, 12)}-${numeros.slice(12, 14)}`
      }
    }
  }

  const handleCpfCnpjChange = (e) => {
    const formatted = formatarCpfCnpj(e.target.value)
    setCpfCnpj(formatted)
  }

  const validarFormulario = () => {
    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setErro('Email inválido')
      return false
    }

    // Validar senha (mínimo 8 chars, 1 maiúscula, 1 número, 1 especial)
    const senhaRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/
    if (!senhaRegex.test(senha)) {
      setErro('Senha deve ter: 8+ caracteres, 1 maiúscula, 1 número e 1 caractere especial')
      return false
    }

    // Validar CPF/CNPJ
    const cpfCnpjNumeros = cpfCnpj.replace(/\D/g, '')
    if (cpfCnpjNumeros.length !== 11 && cpfCnpjNumeros.length !== 14) {
      setErro('CPF/CNPJ inválido')
      return false
    }

    return true
  }

  const tratarErro = (error) => {
    if (error.message.includes('already registered')) {
      return 'Este email já está cadastrado. Faça login ou recupere sua senha.'
    }
    if (error.message.includes('invalid email')) {
      return 'Email inválido. Verifique e tente novamente.'
    }
    if (error.message.includes('weak password') || error.message.includes('Password should be at least')) {
      return 'Senha muito fraca. Use pelo menos 8 caracteres.'
    }
    return error.message || 'Erro ao criar conta. Tente novamente mais tarde.'
  }

  // Validação de senha em tempo real
  const validations = {
    minLength: senha.length >= 8,
    hasUpperCase: /[A-Z]/.test(senha),
    hasNumber: /\d/.test(senha),
    hasSpecial: /[@$!%*?&]/.test(senha)
  }

  const getPasswordStrength = () => {
    const passedCount = Object.values(validations).filter(Boolean).length
    if (passedCount <= 2) return { label: 'Fraca', color: '#ef4444', width: '33%' }
    if (passedCount === 3) return { label: 'Média', color: '#f59e0b', width: '66%' }
    return { label: 'Forte', color: '#22c55e', width: '100%' }
  }

  const strength = getPasswordStrength()

  const handleSocialLogin = async (provider) => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: `${window.location.origin}/app/onboarding`
        }
      })
      if (error) throw error
    } catch (error) {
      console.error('Erro no login social:', error)
      setErro('Erro ao conectar com ' + provider)
    }
  }

  const handleCadastro = async (e) => {
    e.preventDefault()
    setLoading(true)
    setErro('')

    try {
      // 1. Validar formulário completo
      if (!validarFormulario()) {
        setLoading(false)
        return
      }

      // 1.1 Verificar se CPF/CNPJ já existe (via RPC para segurança)
      const { data: cpfExists, error: cpfCheckError } = await supabase
        .rpc('check_cpf_exists', { cpf_check: cpfCnpj })

      if (cpfCheckError) {
        console.error('Erro ao verificar CPF:', cpfCheckError)
        // Se a função não existir (migration não rodou), tentamos seguir
      } else if (cpfExists) {
        setErro('Este CPF/CNPJ já está cadastrado.')
        setLoading(false)
        return
      }

      // 1.2 Verificar se email já existe na tabela de usuários
      const { data: existingUser, error: checkError } = await supabase
        .from('usuarios')
        .select('id')
        .eq('email', email)
        .maybeSingle()
      
      if (checkError) {
        console.error('Erro ao verificar email:', checkError)
        // Não bloqueia, tenta seguir com auth.signUp
      }

      if (existingUser) {
        setErro('Este email já está cadastrado. Faça login.')
        setLoading(false)
        return
      }

      // 2. Criar usuário no Supabase Auth
      // Nota: Passamos metadados mínimos para evitar falhas de tamanho de header ou processamento
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: email,
        password: senha,
        options: {
          emailRedirectTo: `${window.location.origin}/app/onboarding`,
          data: {
            nome_completo: 'A definir', // Essencial para o trigger
            telefone: '',
            cpf_cnpj: cpfCnpj
            // Removemos 'plano' daqui se não for crítico, o trigger já define 'starter' como padrão
          }
        }
      })

      if (authError) throw authError

      // Se a sessão for nula, significa que a confirmação de email é necessária
      if (authData.user && !authData.session) {
        setEmailEnviado(true)
        setLoading(false)
        return
      }

      // A criação dos dados na tabela 'usuarios' e 'controle_planos' agora é feita automaticamente
      // por um Trigger no banco de dados para evitar erros de permissão (RLS).
      
      // Pequeno delay para garantir que o trigger rodou antes de redirecionar (opcional, mas bom pra UX imediata)
      await new Promise(resolve => setTimeout(resolve, 1000));

      // 5. Meta Pixel: Cadastro completo e início de trial
      trackCompleteRegistration()
      trackStartTrial()

      // 6. Login automático já está ativo (sessão criada pelo signUp)
      // 7. Redirecionar para onboarding
      navigate('/app/onboarding')

    } catch (error) {
      console.error('Erro ao cadastrar:', error)
      setErro(tratarErro(error))
    } finally {
      setLoading(false)
    }
  }

  if (emailEnviado) {
    return (
      <div className="auth-page">
        <div className="auth-card wide" style={{ textAlign: 'center' }}>
          <Icon icon="ph:envelope-simple-open" width="64" color="var(--action-primary)" style={{ marginBottom: '20px' }} />
          <h2 className="auth-title" style={{ marginBottom: '16px' }}>Verifique seu e-mail</h2>
          <p className="auth-subtitle" style={{ marginBottom: '24px' }}>
            Enviamos um link de confirmação para <strong>{email}</strong>.<br />
            Por favor, clique no link para ativar sua conta e acessar a plataforma.
          </p>
          <div style={{
            backgroundColor: 'var(--bg-tertiary)',
            padding: '16px',
            borderRadius: '8px',
            fontSize: '14px',
            color: 'var(--text-brand)',
            marginBottom: '24px',
            border: '1px solid var(--border-primary)'
          }}>
            <Icon icon="ph:info" style={{ verticalAlign: 'middle', marginRight: '6px' }} />
            Não encontrou o e-mail? Verifique sua caixa de spam ou lixo eletrônico.
          </div>
          <button
            onClick={() => navigate('/login')}
            className="auth-social-btn"
            style={{ justifyContent: 'center', color: 'var(--action-primary)', borderColor: 'var(--action-primary)' }}
          >
            Voltar para Login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-page">
      <div className="auth-card wide">
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src="/icone.svg" alt="ClickPag" style={{ height: '48px', width: 'auto' }} />
        </div>
        <h2 className="auth-title">
          Criar Conta
        </h2>
        <p className="auth-subtitle">
          Preencha os dados abaixo para começar seu teste grátis
        </p>

        {/* Botões de Login Social */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
          <button
            onClick={() => handleSocialLogin('google')}
            className="auth-social-btn"
          >
            <Icon icon="logos:google-icon" width="20" height="20" />
            Continuar com Google
          </button>

          <button
            onClick={() => handleSocialLogin('facebook')}
            className="auth-social-btn facebook"
          >
            <Icon icon="logos:facebook" width="20" height="20" color="white" />
            Continuar com Facebook
          </button>
        </div>

        <div className="auth-divider">
          <div className="auth-divider-line"></div>
          <span className="auth-divider-text">ou cadastre com e-mail</span>
          <div className="auth-divider-line"></div>
        </div>

        {erro && (
          <div style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: '#ef4444',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            textAlign: 'center',
            border: '1px solid rgba(239, 68, 68, 0.2)'
          }}>
            {erro}
          </div>
        )}

        <form onSubmit={handleCadastro}>
            <div className="auth-input-container">
              <label className="auth-label">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="auth-input"
              />
            </div>

            <div className="auth-input-container">
              <label className="auth-label">
                CPF
              </label>
              <input
                type="text"
                required
                value={cpfCnpj}
                onChange={handleCpfCnpjChange}
                placeholder="000.000.000-00"
                className="auth-input"
              />
            </div>

            <div className="auth-input-container">
              <label className="auth-label">
                Senha
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  className="auth-input"
                  style={{ paddingRight: '40px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-tertiary)',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Icon icon={showPassword ? "ph:eye" : "ph:eye-slash"} width="20" />
                </button>
              </div>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              {/* Barra de Força */}
              {senha && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '12px' }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Força da senha:</span>
                    <span style={{ fontWeight: 'bold', color: strength.color }}>{strength.label}</span>
                  </div>
                  <div style={{ width: '100%', height: '4px', backgroundColor: 'var(--border-primary)', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ width: strength.width, height: '100%', backgroundColor: strength.color, transition: 'all 0.3s ease' }} />
                  </div>
                </div>
              )}

              {/* Lista de Requisitos */}
              <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>A senha deve conter:</p>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: '12px' }}>
                <li style={{ color: validations.minLength ? '#22c55e' : 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                  <span>{validations.minLength ? '✓' : '•'}</span> Mínimo 8 caracteres
                </li>
                <li style={{ color: validations.hasUpperCase ? '#22c55e' : 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                  <span>{validations.hasUpperCase ? '✓' : '•'}</span> Pelo menos 1 letra maiúscula
                </li>
                <li style={{ color: validations.hasNumber ? '#22c55e' : 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                  <span>{validations.hasNumber ? '✓' : '•'}</span> Pelo menos 1 número
                </li>
                <li style={{ color: validations.hasSpecial ? '#22c55e' : 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>{validations.hasSpecial ? '✓' : '•'}</span> Pelo menos 1 caractere especial (@$!%*?&)
                </li>
              </ul>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="auth-button"
            >
              {loading ? 'Criando conta...' : 'Criar Conta'}
            </button>
        </form>

        <p className="auth-footer">
          Já tem uma conta?
          <a
            href="/login"
            className="auth-link"
            style={{ marginLeft: '5px' }}
          >
            Fazer login
          </a>
        </p>
      </div>
    </div>
  )
}
